import React, { useEffect, useState } from "react";
import { personas as personasApi } from "../api/endpoints";
import Spinner from "../components/Spinner";
import Empty from "../components/Empty";
import { TipoPill, EstadoPill } from "../components/Pills";
import CaseDetail from "./case-detail";
import { useDebounced } from "../components/useDebounced";
import { useAuth } from "../auth/AuthContext";
import { rolesOf } from "../utils/permissions";
import { useToast } from "../components/Toast";

// feature/persona-edit — same role set as the PATCH endpoint's
// requireRole. Insumos NOT included. Exported so the inline
// case-detail edit component (InlineEditAccionante) imports the
// SAME list — single source of truth for persona-edit role gating.
export const PERSONA_EDIT_ROLES = ["reparto", "admin", "carpetas", "directora", "abogado"];

export default function Personas() {
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 300);
  const [results, setResults] = useState([]);
  const [chosen, setChosen] = useState(null);

  useEffect(() => {
    if (!dq) { setResults([]); return; }
    personasApi.search(dq).then(r => setResults(r.data));
  }, [dq]);

  return (
    <div className="col" style={{ gap: 16 }}>
      <h1 style={{ margin: 0 }}>Accionantes</h1>
      <div className="muted" style={{ marginTop: -8 }}>Busque por cédula o nombre. La misma persona puede tener múltiples casos.</div>

      <div className="card tight">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cédula o nombre" />
      </div>

      {!dq ? (
        <div className="card"><Empty title="Empiece a escribir" message="Los resultados aparecerán aquí." /></div>
      ) : !results.length ? (
        <div className="card"><Empty title="Sin coincidencias" message="Intente otro nombre o cédula." /></div>
      ) : (
        <div className="grid-2">
          <div className="card" style={{ padding: 0 }}>
            <table>
              <thead><tr><th>Nombre</th><th>Cédula</th></tr></thead>
              <tbody>
                {results.map(p => (
                  <tr key={p.id} onClick={() => setChosen(p)} style={{ cursor: "pointer", background: chosen?.id === p.id ? "var(--accent-mint-bg)" : undefined }}>
                    <td><strong>{p.nombre}</strong></td>
                    <td>{p.cedula || <span className="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card">
            {chosen ? (
              <PersonaDetail
                persona={chosen}
                onUpdated={(updated) => {
                  setChosen(updated);
                  // Refresh search list inline so the table reflects
                  // the rename without needing a re-search.
                  setResults(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
                }}
              />
            ) : <Empty title="Seleccione una persona" />}
          </div>
        </div>
      )}
    </div>
  );
}

function PersonaDetail({ persona, onUpdated }) {
  const [cases, setCases] = useState(null);
  const [openId, setOpenId] = useState(null);
  const { user } = useAuth();
  // feature/persona-edit — multi-role gate via the canonical helper.
  const canEdit = PERSONA_EDIT_ROLES.some(r => rolesOf(user).includes(r));

  useEffect(() => {
    setCases(null);
    personasApi.cases(persona.id).then(r => setCases(r.data));
  }, [persona.id]);

  const grouped = (cases || []).reduce((acc, c) => {
    (acc[c.tipo] = acc[c.tipo] || []).push(c); return acc;
  }, {});

  return (
    <>
      <h2 style={{ marginTop: 0 }}>
        <PersonaField
          persona={persona} field="nombre" canEdit={canEdit}
          display={persona.nombre}
          onUpdated={onUpdated}
        />
      </h2>
      <div className="muted" style={{ fontSize: 12, display: "flex", flexDirection: "column", gap: 4 }}>
        <PersonaField
          persona={persona} field="cedula" canEdit={canEdit}
          display={persona.cedula ? `CC ${persona.cedula}` : "Sin cédula"}
          inputPrefix="CC "
          hint="Cambiar la cédula puede afectar la trazabilidad de casos asociados — verifique antes de guardar."
          onUpdated={onUpdated}
        />
        <PersonaField
          persona={persona} field="email" canEdit={canEdit}
          display={persona.email || "Sin correo"}
          inputType="email"
          onUpdated={onUpdated}
        />
        <PersonaField
          persona={persona} field="telefono" canEdit={canEdit}
          display={persona.telefono || "Sin teléfono"}
          onUpdated={onUpdated}
        />
      </div>
      <div className="rule" />
      {cases == null ? <Spinner /> : !cases.length ? <Empty title="Sin casos" /> : (
        <>
          {Object.entries(grouped).map(([tipo, list]) => (
            <div key={tipo} style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <TipoPill tipo={tipo} />
                <span className="muted" style={{ fontSize: 12 }}>{list.length} caso(s)</span>
              </div>
              <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {list.map(c => (
                  <li key={c.id} style={{ display: "flex", gap: 10, padding: "8px 10px", borderBottom: "1px solid var(--border-hairline)", cursor: "pointer" }}
                      onClick={() => setOpenId(c.id)}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, color: "var(--text-primary)" }}>{c.case_number}</div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{c.radicado} · {c.juzgado || "—"}</div>
                    </div>
                    <EstadoPill c={c} />
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </>
      )}
      <CaseDetail caseId={openId} open={!!openId} onClose={() => setOpenId(null)} />
    </>
  );
}

// feature/persona-edit — single-field inline edit. Renders the display
// string with a pencil affordance when canEdit; click → input
// replaces text → Save calls PATCH with only that field, Cancel
// reverts. 400/409 errors render inline; success briefly flashes a
// green check.
// feature/enum-display-labels — readable Spanish field names for the edit
// tooltip (key stays the DB column name).
const PERSONA_FIELD_LABELS = {
  nombre: "nombre", cedula: "cédula", email: "correo", telefono: "teléfono",
};

function PersonaField({ persona, field, canEdit, display, inputType = "text", inputPrefix, hint, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(persona[field] ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const toast = useToast();

  function startEdit() {
    setVal(persona[field] ?? "");
    setErr(null);
    setEditing(true);
  }
  function cancel() {
    setEditing(false);
    setErr(null);
  }
  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const body = { [field]: val };
      const r = await personasApi.update(persona.id, body);
      setEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      onUpdated?.(r.data);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || "Error al guardar.";
      setErr(msg);
      toast.push(msg, "error");
    } finally {
      setBusy(false);
    }
  }

  if (!canEdit) {
    return <span>{display}</span>;
  }

  if (!editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span>{display}</span>
        <button
          type="button"
          className="ghost sm"
          style={{ fontSize: 11, padding: "2px 8px" }}
          onClick={startEdit}
          title={`Editar ${PERSONA_FIELD_LABELS[field] || field}`}
        >Editar</button>
        {savedFlash && (
          <span style={{ color: "var(--accent-mint)", fontSize: 12 }} aria-hidden="true">✓</span>
        )}
      </span>
    );
  }

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {inputPrefix && <span className="muted" style={{ fontSize: 12 }}>{inputPrefix}</span>}
        <input
          type={inputType}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          autoFocus
          disabled={busy}
          style={{ minWidth: 200 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") cancel();
          }}
        />
        <button type="button" disabled={busy} onClick={save} style={{ fontSize: 12, padding: "2px 10px" }}>
          {busy && <Spinner color="currentColor" />} Guardar
        </button>
        <button type="button" className="secondary" disabled={busy} onClick={cancel} style={{ fontSize: 12, padding: "2px 10px" }}>
          Cancelar
        </button>
      </div>
      {hint && (
        <div className="muted" style={{ fontSize: 11, fontStyle: "italic" }}>{hint}</div>
      )}
      {err && (
        <div style={{ fontSize: 11, color: "var(--color-danger)" }}>{err}</div>
      )}
    </div>
  );
}
